import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { jwtDecode } from 'jwt-decode'
import type { AuthState, User } from '@models/index'

const initialState: AuthState = {
  token: null,
  user: null,
  isAuthenticated: false,
  loading: true,
  error: null,
}

// Async thunk to validate JWT token
export const validateToken = createAsyncThunk(
  'auth/validateToken',
  async (token: string, { rejectWithValue }) => {
    try {
      // Decode JWT to extract user info
      const decoded: any = jwtDecode(token)

      // Check if token is expired
      const currentTime = Date.now() / 1000
      if (decoded.exp && decoded.exp < currentTime) {
        throw new Error('Token expired')
      }

      // Store token in localStorage
      localStorage.setItem('jwt_token', token)

      return {
        token,
        user: {
          username: decoded.sub || decoded.username || 'User',
          email: decoded.email,
          id: decoded.user_id,
        } as User,
      }
    } catch (error) {
      localStorage.removeItem('jwt_token')
      return rejectWithValue('Invalid or expired token')
    }
  }
)

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.token = null
      state.user = null
      state.isAuthenticated = false
      localStorage.removeItem('jwt_token')
    },
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(validateToken.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(validateToken.fulfilled, (state, action: PayloadAction<{ token: string; user: User }>) => {
        state.loading = false
        state.token = action.payload.token
        state.user = action.payload.user
        state.isAuthenticated = true
        state.error = null
      })
      .addCase(validateToken.rejected, (state, action) => {
        state.loading = false
        state.token = null
        state.user = null
        state.isAuthenticated = false
        state.error = action.payload as string
      })
  },
})

export const { logout, clearError } = authSlice.actions
export default authSlice.reducer
